// src/pages/finance-portal/credit-memos/CreditMemos.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tabs, Tab,
    Tooltip, InputAdornment, Autocomplete, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox,
} from '@mui/material';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    APPLIED: { bg: '#dcfce7', color: '#166534' },
    VOID: { bg: '#fecaca', color: '#991b1b' },
};

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Applied', value: 'APPLIED' },
    { label: 'Void', value: 'VOID' },
];

const REFUND_METHOD_LABELS = {
    ORIGINAL_PAYMENT: 'Original Payment',
    ACCOUNT_CREDIT: 'Account Credit',
    MANUAL_CHECK: 'Manual Check',
    NONE: 'None',
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function CreditMemos() {
    const navigate = useNavigate();
    const [memos, setMemos] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    // Apply dialog
    const [applyDialog, setApplyDialog] = useState({ open: false, memo: null });
    const [applying, setApplying] = useState(false);
    const [applyInvoice, setApplyInvoice] = useState(null);
    const [applyInvoiceOptions, setApplyInvoiceOptions] = useState([]);
    const [applyInvoiceSearch, setApplyInvoiceSearch] = useState('');
    const [applyInvoiceLoading, setApplyInvoiceLoading] = useState(false);
    const [createJournalEntry, setCreateJournalEntry] = useState(false);
    const applyInvoiceTimer = useRef(null);

    // Void dialog
    const [voidDialog, setVoidDialog] = useState({ open: false, memo: null });
    const [voiding, setVoiding] = useState(false);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, memo: null });
    const [deleting, setDeleting] = useState(false);

    const fetchMemos = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (statusFilter) params.status = statusFilter;
            if (search) params.search = search;

            const res = await axiosInstance.get('/api/finance/credit-memos', { params });
            if (res.data.success) {
                setMemos(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load credit memos');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusFilter, search]);

    useEffect(() => { fetchMemos(); }, [fetchMemos]);

    // Fetch unpaid invoices for apply dialog (filtered by same customer)
    useEffect(() => {
        if (applyInvoiceTimer.current) clearTimeout(applyInvoiceTimer.current);
        if (!applyDialog.open || !applyDialog.memo?.customer_id) {
            setApplyInvoiceOptions([]);
            return;
        }
        setApplyInvoiceLoading(true);
        applyInvoiceTimer.current = setTimeout(async () => {
            try {
                const params = {
                    customer_id: applyDialog.memo.customer_id,
                    status: 'unpaid',
                };
                if (applyInvoiceSearch) params.search = applyInvoiceSearch;
                const res = await axiosInstance.get('/api/finance/invoices', { params });
                if (res.data.success) {
                    setApplyInvoiceOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch invoices', err);
            } finally {
                setApplyInvoiceLoading(false);
            }
        }, 300);
        return () => { if (applyInvoiceTimer.current) clearTimeout(applyInvoiceTimer.current); };
    }, [applyDialog.open, applyDialog.memo?.customer_id, applyInvoiceSearch]);

    const openApplyDialog = (memo) => {
        setApplyDialog({ open: true, memo });
        setApplyInvoice(null);
        setApplyInvoiceSearch('');
        setCreateJournalEntry(false);
    };

    const handleApply = async () => {
        setApplying(true);
        setError(null);
        try {
            const payload = {
                create_journal_entry: createJournalEntry,
            };
            if (applyInvoice) {
                payload.invoice_id = applyInvoice.id;
            }
            await axiosInstance.post(`/api/finance/credit-memos/${applyDialog.memo.id}/apply`, payload);
            setApplyDialog({ open: false, memo: null });
            setSuccess('Credit memo applied successfully');
            fetchMemos();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to apply credit memo');
        } finally {
            setApplying(false);
        }
    };

    const handleVoid = async () => {
        setVoiding(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/credit-memos/${voidDialog.memo.id}/void`);
            setVoidDialog({ open: false, memo: null });
            setSuccess('Credit memo voided successfully');
            fetchMemos();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to void credit memo');
        } finally {
            setVoiding(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            await axiosInstance.delete(`/api/finance/credit-memos/${deleteDialog.memo.id}`);
            setDeleteDialog({ open: false, memo: null });
            setSuccess('Credit memo deleted successfully');
            fetchMemos();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete credit memo');
        } finally {
            setDeleting(false);
        }
    };

    const tabIndex = STATUS_TABS.findIndex(t => t.value === statusFilter);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CreditScoreIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Credit Memos</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Manage customer credit memos and refunds</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/finance-portal/credit-memos/new')}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        Create Credit Memo
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Status Tabs */}
                <Tabs
                    value={tabIndex >= 0 ? tabIndex : 0}
                    onChange={(_, idx) => { setStatusFilter(STATUS_TABS[idx].value); setPage(0); }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            fontFamily, textTransform: 'none', fontWeight: 600,
                            fontSize: '0.85rem', color: '#64748b', minHeight: 40,
                        },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    {STATUS_TABS.map(t => <Tab key={t.value} label={t.label} />)}
                </Tabs>

                {/* Search */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search credit memos..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: 280 }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['CM #', 'Customer', 'Original Invoice', 'Date', 'Total', 'Refund Method', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : memos.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <CreditScoreIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No credit memos found</Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => navigate('/finance-portal/credit-memos/new')}
                                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1 }}
                                            >
                                                Create your first credit memo
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ) : memos.map(memo => {
                                    const statusStyle = STATUS_COLORS[memo.status] || {};
                                    const customerName = memo.customer_name || memo.customer_company || '\u2014';
                                    return (
                                        <TableRow
                                            key={memo.id}
                                            hover
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                                            onClick={() => navigate(`/finance-portal/credit-memos/${memo.id}`)}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {memo.credit_memo_number || '\u2014'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {customerName}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {memo.original_invoice_number || '\u2014'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(memo.credit_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(memo.total)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {REFUND_METHOD_LABELS[memo.refund_method] || memo.refund_method || '\u2014'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(memo.status)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell onClick={e => e.stopPropagation()}>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="View">
                                                        <IconButton size="small" onClick={() => navigate(`/finance-portal/credit-memos/${memo.id}`)} sx={{ color: '#94a3b8' }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {memo.status === 'DRAFT' && (
                                                        <Tooltip title="Apply">
                                                            <IconButton size="small" onClick={() => openApplyDialog(memo)} sx={{ color: '#94a3b8', '&:hover': { color: '#22c55e' } }}>
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(memo.status === 'DRAFT' || memo.status === 'APPLIED') && (
                                                        <Tooltip title="Void">
                                                            <IconButton size="small" onClick={() => setVoidDialog({ open: true, memo })} sx={{ color: '#94a3b8', '&:hover': { color: '#f59e0b' } }}>
                                                                <BlockIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {memo.status === 'DRAFT' && (
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, memo })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Apply Confirmation Dialog */}
            <Dialog open={applyDialog.open} onClose={() => setApplyDialog({ open: false, memo: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Apply Credit Memo {applyDialog.memo?.credit_memo_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', mb: 2 }}>
                        This will mark the credit memo as applied. You can optionally apply it to an unpaid invoice for the same customer.
                    </Typography>
                    <Autocomplete
                        size="small"
                        options={applyInvoiceOptions}
                        getOptionLabel={opt => opt ? `${opt.invoice_number || 'INV-???'} - ${formatCurrency(opt.total)}` : ''}
                        value={applyInvoice}
                        onChange={(_, val) => setApplyInvoice(val)}
                        onInputChange={(_, val) => setApplyInvoiceSearch(val)}
                        loading={applyInvoiceLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Apply to Invoice (optional)"
                                placeholder="Search unpaid invoices..."
                                InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { fontFamily, borderRadius: '10px' },
                                    endAdornment: (
                                        <>
                                            {applyInvoiceLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText="No unpaid invoices found"
                        sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={createJournalEntry}
                                onChange={e => setCreateJournalEntry(e.target.checked)}
                                sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                            />
                        }
                        label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Create Journal Entry</Typography>}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setApplyDialog({ open: false, memo: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        disabled={applying}
                        startIcon={applying ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {applying ? 'Applying...' : 'Apply'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Void Confirmation Dialog */}
            <Dialog open={voidDialog.open} onClose={() => setVoidDialog({ open: false, memo: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Void Credit Memo {voidDialog.memo?.credit_memo_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will void the credit memo. This action cannot be undone. The credit memo will no longer be applicable.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setVoidDialog({ open: false, memo: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleVoid}
                        disabled={voiding}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {voiding ? 'Voiding...' : 'Void Credit Memo'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, memo: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Credit Memo {deleteDialog.memo?.credit_memo_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently delete the draft credit memo and all its line items. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, memo: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
